
import { useState, useCallback, useEffect } from 'react';
import { FichaTecnica, FichaTecnicaItem } from '../types';
import { supabaseService } from '../supabaseService';

export const useFichaTecnica = (produtoId?: string) => {
  const [ficha, setFicha] = useState<FichaTecnica | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFicha = useCallback(async () => {
    if (!produtoId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await supabaseService.getFichaTecnicaByProduto(produtoId);
      setFicha(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [produtoId]);

  const saveFicha = async (dados: Partial<FichaTecnica>) => {
    setLoading(true);
    try {
      const saved = await supabaseService.salvarFichaTecnica({ ...dados, produto_id: produtoId });
      await loadFicha();
      return saved;
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const addItem = async (item: Partial<FichaTecnicaItem>) => {
    if (!ficha?.id) return;
    setLoading(true);
    try {
      await supabaseService.salvarItemFichaTecnica({ ...item, ficha_tecnica_id: ficha.id });
      await loadFicha();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (itemId: string) => {
    setLoading(true);
    try {
      await supabaseService.removerItemFichaTecnica(itemId);
      await loadFicha();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const calculateCost = async () => {
    if (!ficha?.id) return;
    setLoading(true);
    try {
      await supabaseService.recalcularCusto(ficha.id);
      await loadFicha();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (produtoId) {
      loadFicha();
    }
  }, [produtoId, loadFicha]);

  return {
    ficha,
    loading,
    error,
    reload: loadFicha,
    saveFicha,
    addItem,
    removeItem,
    calculateCost
  };
};
